import type {CellFormatter, ComponentCell, DataColumn, DataGetter, DataGetterRule, JoinFormatter} from "@t/fd/layout/Agenda";
import type {FormData} from "@t/fd/FormPart"
import {getter} from "../../Part";
import {cast} from "../../editors/Editor";
import {formatterOf} from "./Formatters";
import DataCell from "./DataCell.vue";
import HeaderCell from "./HeaderCell.vue";
import SelectionHeaderCell from "./SelectionHeaderCell.vue";
import SelectionCell from "./SelectionCell.vue";
import type {ComputedRef, CSSProperties, InjectionKey, Ref} from "vue";
import {computed, inject} from "vue";
import {Column, ColumnLayout, ColumnType, LayoutMode} from "@t/agenda/Columns.ts";
import {ItemsGrid} from "@t/fd/layout/Items.ts";
import JoinedCells from "@fd/containers/agenda/JoinedCells.vue";
import DeletionHeaderCell from "@fd/containers/agenda/DeletionHeaderCell.vue";
import DeletionCell from "@fd/containers/agenda/DeletionCell.vue";
import FormEditorCell from "@fd/containers/agenda/FormEditorCell.vue";
import SortingHeaderCell from "@fd/containers/agenda/SortingHeaderCell.vue";
import SortingCell from "@fd/containers/agenda/SortingCell.vue";

/**
 * Interface for cells to coordinate with agenda part
 */
export interface CellAPI {
    /** Return info if the column with this name is sorted and in what direction */
    getSorting: (colName:string) => "no" | "asc" | "desc";
    setSorting: (colName:string, type: "no" | "asc" | "desc", append?:boolean) => void;
	action: (name:string, key:number|string, event:MouseEvent) => void;
}

export const itemsGridKey = Symbol() as InjectionKey<ComputedRef<ItemsGrid>>;
export const rowObjectKey = Symbol() as InjectionKey<number|string>;
export const cellApiKey = Symbol() as InjectionKey<CellAPI>;

export function injectItemsGrid():ComputedRef<ItemsGrid> {
    const ap = inject(itemsGridKey);
    if (ap === undefined) throw "Error injecting agenda/items (probably a bug)";
    return ap;
}

export function injectCellAPI():CellAPI {
    const capi = inject(cellApiKey);
    if (capi === undefined) throw "Cell API not provided (internal bug)";
    return capi;
}

export interface RowGroupStructure {
	/** Total number of columns */
	cols: number;
	colStyles?: CSSProperties[];
	tableStyle?: CSSProperties;

	/** Structure of a regular row */
	row: RowStructure[];
	/** Structure of a header row */
	header: RowStructure[];
	/** Structure of an editor row */
	editor?: RowStructure[];
}

export interface RowStructure {
    cells: CellStructure[]
}

export interface CellBinding {
    [key:string]:any;
}

export interface CellComponent {
	component: any;
	bind?: CellBinding;
}

export interface CellStructure extends CellComponent{
    rowspan: number;
    colspan: number;
}

function findColumnByFullName(colName:string, all:DataColumn[]):DataColumn|null {
    for (let c of all) {
        if (!c.fullName) continue;
        if (c.fullName === colName) return c;
    }
    return null;
}

function findColumnByName(colName:string, all:DataColumn[]):DataColumn|null {
    for (let c of all) {
        if (c.name === colName) return c;
    }
    return null;
}

export function findColumn(colName:string|undefined|null, all:DataColumn[]):DataColumn|null {
    if (colName == null) return null;
    const dot = colName.indexOf('.');
    let found = null;
    if (dot > 0) {
        // columnName is in table.col format
        found = findColumnByFullName(colName, all);
        if (found === null) found = findColumnByName(colName.substring(dot+1), all);    // use name only
    } else {
        found = findColumnByName(colName, all);
    }
    return found;
}

function bind<T extends Partial<CellComponent>>(cc:T, key:string, value: any):T {
	if (typeof cc.bind === "undefined") cc.bind = {};
	cc.bind[key] = value;
	return cc;
}


const SELECTION:ColumnLayout = {
	name: "_selection",
	type: ColumnType.SELECT,
	layout: "left",
	fixedWidth: "20px" /* Set to the size of the select icon */
};

const DELETION:ColumnLayout = {
	name: "_deletion",
	type: ColumnType.DELETE,
	layout: "right",
	fixedWidth: "18px" /* Set to the size of the trash icon */
};

const SORTING:ColumnLayout = {
	name: "_sorting",
	type: ColumnType.SORT,
	layout: "right",
	fixedWidth: "22px" /* Set to the size of the menu-18 icon */
};

function oneRowCell(s:Partial<CellComponent>, c:ColumnLayout, grid:ItemsGrid):void {
	if (c.type === ColumnType.SELECT) {
		s.component = SelectionCell;
		s.bind = { col: c };
	} else if (c.type === ColumnType.DELETE) {
		s.component = DeletionCell;
		s.bind = { col: c };
	} else if (c.type === ColumnType.SORT) {
		s.component = SortingCell;
		s.bind = { col: c };
	} else {
		// Data column
		s.component = DataCell;
		s.bind = { col: c };
	}
}

function colToRowCell(grid:ItemsGrid, c:ColumnLayout|ColumnLayout[], colspan:number = 1, rowspan:number = 1):CellStructure {
	const s: Partial<CellStructure> = {
		colspan: colspan,
		rowspan: rowspan
	};
	if (Array.isArray(c)) {
		s.component = JoinedCells;
		s.bind = { col: c, tag: "td" };
		const components:CellComponent[] = [];
		for (let sc of c) {
			const comp:Partial<CellComponent> = {};
			oneRowCell(comp, sc, grid);
			bind(comp, "tag", "span");
			components.push(comp as CellComponent);
		}
		s.bind.items = components;
	} else {
		oneRowCell(s as CellStructure, c, grid);
	}
	return s as CellStructure;
}

function rowEditorCell(grid:ItemsGrid, colspan:number, readonly: boolean):CellStructure {
	return {
		colspan: colspan,
		rowspan: 1,
		component: FormEditorCell,
		bind: { tag:"td", grid: grid, readonly }
	};
}

function oneHeaderCell(s:Partial<CellComponent>, c:ColumnLayout, grid:ItemsGrid):void {
	if (c.type === ColumnType.SELECT) {
		s.component = SelectionHeaderCell;
		s.bind = { col: c };
	} else if (c.type === ColumnType.DELETE) {
		s.component = DeletionHeaderCell;
		s.bind = { col: c };
	} else if (c.type === ColumnType.SORT) {
		s.component = SortingHeaderCell;
		s.bind = { col: c };
	} else {
		// Data column
		s.component = HeaderCell;
		s.bind = { col: c, sortable: (grid.sorting === "yes" && !!(c as Column).canSort) };
	}
}

function colToHeaderCell(grid:ItemsGrid, c:ColumnLayout|ColumnLayout[], colspan:number = 1, rowspan:number = 1):CellStructure {
	const s:Partial<CellStructure> = {
		colspan: colspan,
		rowspan: rowspan
	};
	if (Array.isArray(c)) {
		s.component = JoinedCells;
		s.bind = { col: c, tag: "th" };
		const components:CellComponent[] = [];
		for (let sc of c) {
			const comp:Partial<CellComponent> = {};
			oneHeaderCell(comp, sc, grid);
			bind(comp, "tag", "span");
			components.push(comp as CellComponent);
		}
		s.bind.items = components;
	} else {
		oneHeaderCell(s as CellStructure, c, grid);
	}
	return s as CellStructure;
}

export interface TextValues {
	icon?: string;
	values: string[];
}

export function extractTextValues(grid:ItemsGrid, cols: AoOoA<ColumnLayout>, object:any, max:number = 10):TextValues {
	const vals:TextValues = { values:[] };

	for (let col of cols) {
		if (Array.isArray(col)) {
			if (col.length > 0) {
			}
		} else {
		}
	}

	return vals;
}

/** Array of Objects OR Array-of-Objects */
type AoOoA<T> = (T|T[])[];

export function calculateStructure(grid: ItemsGrid, cols: AoOoA<ColumnLayout>, readonly = false):RowGroupStructure {

	// Insert sticky selection color as the first
	if (grid.selectable || (!readonly && (grid.deletable || grid.sorting === "manual"))) {
		const mod:AoOoA<ColumnLayout> = [];
		if (grid.selectable) mod.push(SELECTION);
		mod.push(...cols);
		if (!readonly) {
			if (grid.deletable) {}mod.push(DELETION);
			if (grid.sorting === "manual") mod.push(SORTING);
		}
		cols = mod;

		// Debug code - try to join the first 3 cells
		//cols.splice(0, 0, cols.splice(0, 3) as ColumnLayout[]);
	}



	// Separate columns which stick to left/right/top/bottom
	const left:AoOoA<ColumnLayout> = [];
	const right:AoOoA<ColumnLayout> = [];
	const top:AoOoA<ColumnLayout> = [];
	const bottom:AoOoA<ColumnLayout> = [];
	const normal:AoOoA<ColumnLayout> = [];

	let hasFixedColumn = false;

	const sorter = (col:ColumnLayout|ColumnLayout[], position:LayoutMode) => {
		if (position) {
			switch (position) {
				case "left":
					left.push(col);
					hasFixedColumn = hasFixedColumn || (!Array.isArray(col) && !!col.fixedWidth);
					break;
				case "right":
					right.push(col);
					hasFixedColumn = hasFixedColumn || (!Array.isArray(col) && !!col.fixedWidth);
					break;
				case "top":
					top.push(col);
					break;
				case "bottom":
					bottom.push(col);
					break;
				default:
					normal.push(col);
					hasFixedColumn = hasFixedColumn || (!Array.isArray(col) && !!col.fixedWidth);
			}
		} else normal.push(col);

	};

	for (let col of cols) {
		if (Array.isArray(col)) {
			if (col.length > 0) {
				// layout out the col group will be determined by the first item in the group
				const layout = col[0].layout;
				sorter(col, layout);
			}
		} else {
			sorter(col, col.layout);
		}
	}
	// Do we need to break normal columns into multiple runs
	let runs:AoOoA<ColumnLayout>[] = [ normal ];
	let normalCount = normal.length;

	// Insert top columns to runs
	for (let i = 0; i < top.length; i++) {
		runs.splice(i, 0, [ top[i] ]);
	}
	// Append bottom columns to runs
	for (let b of bottom) {
		runs.push([ b ]);
	}

	//----
    const rowStructure:RowStructure[] = [];
    const headerStructure:RowStructure[] = [];
    const editorStructure:RowStructure[] = [];

	let first:boolean = true;
	for (let r of runs) {
		const rowCells:CellStructure[] = [];
		const headerCells:CellStructure[] = [];
		const editorCells:CellStructure[] = [];
		if (first) {
			// Insert sticky left columns
			for (let c of left) {
				headerCells.push(colToHeaderCell(grid, c, 1, runs.length));
				let rc = colToRowCell(grid, c, 1, runs.length);
				rowCells.push(rc);
				editorCells.push(rc);
			}
		}
		let needSpan = normalCount;
		for (let i = 0; i < r.length; i++) {
			const span =  i == (r.length - 1) ? needSpan : 1;	// last column takes the rest
			const c = r[i];
			rowCells.push(colToRowCell(grid, c, span));
			headerCells.push(colToHeaderCell(grid, c, span));
			needSpan -= span;
		}
		if (first) editorCells.push(rowEditorCell(grid, normalCount, readonly));

		if (first) {
			// Insert sticky right columns
			for (let c of right) {
				headerCells.push(colToHeaderCell(grid, c, 1, runs.length));
				let rc = colToRowCell(grid, c, 1, runs.length);
				rowCells.push(rc);
				editorCells.push(rc);
			}
		}
		rowStructure.push({ cells: rowCells });
		headerStructure.push({ cells: headerCells });
		if (first) {
			editorStructure.push({ cells: editorCells });
			first = false;
		}
	}
	let result:RowGroupStructure = { row: rowStructure, header: headerStructure, editor: editorStructure, cols: normalCount + left.length + right.length };
	if (hasFixedColumn) {
		//result.tableStyle = { tableLayout: "fixed" };
		result.colStyles = generateColumnStyles(left, normal, right);
	}
	return result;
}

function generateColumnStyles(left, normal, right):CSSProperties[] {
	const styles: CSSProperties[] = [];
	const cols = [...left, ...normal, ...right];
	for (let c of cols) {
		const s:CSSProperties = {};
		if (c.fixedWidth) {
			s.width = "calc("+c.fixedWidth+" + 2 * var(--agenda-h-padding))";
		}
		styles.push(s);
	}
	return styles;
}

function isDynamic(rule:DataGetter):rule is DataGetterRule {
    return rule.hasOwnProperty("d");
}

export function extractDataVariable(obj: any, g: DataGetter, formData: FormData):any {
    if (typeof g === "object") {
        let val: any;
        if (isDynamic(g)) {
            val = getter(obj, g.d, formData);
            if (val === undefined) {
                if (g.ifmissing) val = g.ifmissing;
            }
        } else {
            val = g.value;
        }
        if (g.c) val = cast(g.c, val);
        return val;
    } else {
        return g;    // static value directly
    }
}

function extractVariableName(idx: number, g: DataGetter) {
    if (typeof g === "object" && g.name) {
        return g.name;
    } else {
        return "$"+idx;    // static value directly
    }
}

export function cellData(obj:any, col:DataColumn, formData:FormData):Map<string, any> {
    const output = new Map<string, any>();
    if (Array.isArray(col.get)) {
        let idx = 0;
        for (let g of col.get) {
            let val = extractDataVariable(obj, g, formData);
            let varName = extractVariableName(idx, g);
            output.set(varName, val);
            idx++;
        }
    } else {
        // implicit strategy for obtaining data
        const prop = !!col.property ? col.property : col.name;
        const val = getter(obj, prop, formData);
        output.set("$0", val);
    }
    return output;
}

function applyFormat(data:Map<string, any>, format: CellFormatter|ComponentCell):string {
    return formatterOf(format).format(data).toString();
}

export function formatData(data:Map<string, any>, col:DataColumn) {
    if (col.format) {
        return applyFormat(data, col.format);
    } else {
        return applyFormat(data, <JoinFormatter>{
            type: "join"
        })
    }
}

const PIXELS_PER_BUDGET_UNIT = 8.83;	// 100ch 14px Inter normal = 883px
export const AGENDA_START_PADDING = 48;	// 2 x --form-h-start-padding

export function useItemsGrid(grid:ComputedRef<ItemsGrid>, containerWidth:Ref<number>, readonly:ComputedRef<boolean>) {

	const columns = computed<AoOoA<DataColumn>>(() => {
		if (grid.value.view && Array.isArray(grid.value.view.columns)) {
			const dc:AoOoA<DataColumn> = [];
			for (let colName of grid.value.view.columns) {
				if (typeof colName === "string") {
					const found = findColumn(colName, grid.value.columns);
					if (found != null) dc.push(found);
				} else {
					const jdc:DataColumn[] = [];
					for (let joinColName of colName) {
						const found = findColumn(joinColName, grid.value.columns);
						if (found != null) jdc.push(found);
					}
					dc.push(jdc);
				}
			}
			return dc;
		}
		return grid.value.columns;
	});

	const structure = computed<RowGroupStructure>(() => {
		console.log("Building row structure: " + readonly.value);
		return calculateStructure(grid.value, columns.value, readonly.value);
	});


	function cellBind(c) {
		const o:any = c.bind ? { ...c.bind } : {};
		if (c.rowspan > 1) o.rowspan = c.rowspan;
		if (c.colspan > 1) o.colspan = c.colspan;
		return o;
	}

	return { structure, columns, cellBind };
}